'use client';

import React, { useState } from 'react';
import { Zap, Linkedin, MessageSquare, CalendarCheck, Clock, ChevronRight, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


interface NBA {
  id: string;
  priority: 'urgent' | 'high' | 'medium';
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  label: string;
  count: number;
  sublabel: string;
  action: string;
}

const recommendations: NBA[] = [
  {
    id: 'nba-linkedin-invite',
    priority: 'urgent',
    icon: Linkedin,
    iconColor: 'text-sky-600',
    iconBg: 'bg-sky-50',
    label: 'Send LinkedIn invites',
    count: 22,
    sublabel: 'High-priority contacts not yet connected',
    action: 'View contacts',
  },
  {
    id: 'nba-followup-overdue',
    priority: 'urgent',
    icon: Clock,
    iconColor: 'text-red-600',
    iconBg: 'bg-red-50',
    label: 'Post-show follow-up overdue',
    count: 8,
    sublabel: 'Contacts met at event with no follow-up',
    action: 'View list',
  },
  {
    id: 'nba-meeting-reminder',
    priority: 'high',
    icon: CalendarCheck,
    iconColor: 'text-emerald-600',
    iconBg: 'bg-emerald-50',
    label: 'Send meeting reminders',
    count: 4,
    sublabel: 'Confirmed meetings within 5 days',
    action: 'View meetings',
  },
  {
    id: 'nba-intro-message',
    priority: 'high',
    icon: MessageSquare,
    iconColor: 'text-indigo-600',
    iconBg: 'bg-indigo-50',
    label: 'Send intro messages',
    count: 14,
    sublabel: 'Connected on LinkedIn, no message sent',
    action: 'Draft message',
  },
  {
    id: 'nba-share-content',
    priority: 'medium',
    icon: Zap,
    iconColor: 'text-violet-600',
    iconBg: 'bg-violet-50',
    label: 'Share thought leadership',
    count: 11,
    sublabel: 'Contacts at intro stage — ready for content',
    action: 'Select content',
  },
];

const priorityLabel: Record<string, string> = {
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
};

const priorityBadge: Record<string, string> = {
  urgent: 'bg-red-50 text-red-700',
  high: 'bg-amber-50 text-amber-700',
  medium: 'bg-blue-50 text-blue-700',
};

export default function NextBestActions() {
  const [dismissed, setDismissed] = useState<string[]>([]);

  const visible = recommendations.filter((r) => !dismissed.includes(r.id));

  return (
    <div className="bg-white rounded-xl border border-border shadow-card p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-6 h-6 rounded-md bg-amber-100 flex items-center justify-center">
          <Zap size={13} className="text-amber-600" />
        </div>
        <h2 className="text-sm font-semibold text-foreground">Next Best Actions</h2>
        <span className="ml-auto text-[10px] font-semibold font-mono bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
          {visible.length} pending
        </span>
      </div>
      <div className="space-y-2">
        {visible.length === 0 ? (
          <div className="flex flex-col items-center py-6 text-center">
            <CheckCircle size={24} className="text-emerald-500 mb-2" />
            <p className="text-xs font-semibold text-foreground">All caught up!</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">No pending actions right now.</p>
          </div>
        ) : (
          visible.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.id}
                className="flex items-start gap-3 p-2.5 rounded-lg border border-transparent hover:bg-muted/50 hover:border-border transition-all duration-150 group cursor-pointer"
                onClick={() => toast.info(`Opening: ${item.label}`, { description: `${item.count} contacts need attention` })}
              >
                <div className={`w-8 h-8 rounded-lg ${item.iconBg} flex items-center justify-center shrink-0 mt-0.5`}>
                  <Icon size={15} className={item.iconColor} strokeWidth={1.8} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className={`text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded ${priorityBadge[item.priority]}`}>
                      {priorityLabel[item.priority]}
                    </span>
                    <span className="text-xs font-semibold text-foreground font-mono">{item.count}</span>
                  </div>
                  <p className="text-xs font-medium text-foreground truncate">{item.label}</p>
                  <p className="text-[10px] text-muted-foreground">{item.sublabel}</p>
                </div>
                <ChevronRight size={13} className="text-muted-foreground mt-2 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}